import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["panel", "continueButton", "limitAlert"];
  static values = { validateUrl: String };

  connect() {
    this.abortController = null;

    this.boundRowRemoved = () => this.validate();
    this.boundOnFocusOut = (e) => this.onFocusOut(e);
    this.boundOnKeydown = (e) => this.onKeydown(e);

    this.element.addEventListener("focusout", this.boundOnFocusOut, true);
    this.element.addEventListener("keydown", this.boundOnKeydown, true);
    this.element.addEventListener(
      "invite-producers-row-removed",
      this.boundRowRemoved,
    );
  }

  disconnect() {
    this.element.removeEventListener("focusout", this.boundOnFocusOut, true);
    this.element.removeEventListener("keydown", this.boundOnKeydown, true);
    this.element.removeEventListener(
      "invite-producers-row-removed",
      this.boundRowRemoved,
    );

    this.abortController?.abort();
  }

  onFocusOut(event) {
    const el = event.target;
    if (!(el instanceof HTMLInputElement)) return;
    if (!el.closest("[data-invite-row-edit]")) return;
    const name = el.name || "";
    if (!name.includes("facility_id") && !name.includes("calculation_id") && !name.includes("email")) return;

    setTimeout(() => {
      const row = el.closest("[data-invite-row]");
      if (!row) return;
      const edit = row.querySelector("[data-invite-row-edit]");
      if (edit?.contains(document.activeElement)) return;
      this.validate();
    }, 0);
  }

  onKeydown(event) {
    if (event.key !== "Enter") return;
    const el = event.target;
    if (!(el instanceof HTMLInputElement)) return;
    if (!el.closest("[data-invite-row-edit]")) return;
    const name = el.name || "";
    if (!name.includes("facility_id") && !name.includes("calculation_id") && !name.includes("email")) return;
    event.preventDefault();
    this.validate();
  }

  async validate() {
    if (!this.hasValidateUrlValue || !this.hasPanelTarget) return;

    this.abortController?.abort();
    this.abortController = new AbortController();

    const token = document.querySelector('meta[name="csrf-token"]')?.content;
    const formData = new FormData(this.element);

    try {
      const response = await fetch(this.validateUrlValue, {
        method: "POST",
        body: formData,
        signal: this.abortController.signal,
        credentials: "same-origin",
        headers: {
          Accept: "application/json",
          ...(token ? { "X-CSRF-Token": token } : {}),
        },
      });

      const data = await response.json().catch(() => ({}));

      if (
        response.ok &&
        typeof data.all_matched === "boolean" &&
        data.facilities_html
      ) {
        this._clearLimitAlert();
        this.panelTarget.innerHTML = data.facilities_html;
        this.syncContinueButton(data.all_matched);
        return;
      }

      if (!response.ok) {
        if (data.invite_limit_alert && this.hasLimitAlertTarget) {
          this.limitAlertTarget.textContent = data.invite_limit_alert;
          this.limitAlertTarget.classList.remove("hidden");
        } else {
          this._clearLimitAlert();
        }
        this.syncContinueButton(false);
      }
    } catch (e) {
      if (e.name === "AbortError") return;
    }
  }

  syncContinueButton(allMatched) {
    if (!this.hasContinueButtonTarget) return;
    this.continueButtonTarget.disabled = !allMatched;
  }

  _clearLimitAlert() {
    if (!this.hasLimitAlertTarget) return;
    this.limitAlertTarget.textContent = "";
    this.limitAlertTarget.classList.add("hidden");
  }
}
